import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'react-covideo-common';
import { BsPlus } from 'react-icons/bs';
import { MdEdit } from 'react-icons/md';
import styled, { css } from 'styled-components/macro';
import { getSecondaryColor } from 'lib/context/theme/utils';
import { useCDK } from 'lib/hooks/useCdk';

const Container = styled.div`
  padding: 24px;
  background-color: ${({ theme }) => theme.colors.white[100]};
  border-radius: 6px;
  width: 50%;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => getSecondaryColor(theme)};
  font-weight: bold;
  gap: 12px;
  font-size: 16px;
`;

const StatusContainer = styled.div<{ isActive: boolean }>`
  background-color: ${({ theme }) => theme.colors.danger.non_alpha};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.danger[80]};
  padding: 2px 8px;
  font-weight: 600;
  font-size: 12px;
  line-height: 20px;
  ${({ isActive, theme }) =>
    isActive &&
    css`
      background-color: ${theme.colors.success[60]};
      color: ${theme.colors.success[100]};
    `}
`;

const BodyContainer = styled.div`
  margin-top: 18px;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
`;

const ButtonContainer = styled.div`
  margin-top: 24px;
  display: flex;
  align-items: center;
  button {
    font-weight: bold;
  }
  gap: 8px;
`;

type RecurringReportProps = {
  title: string;
  isActive?: boolean;
  icon?: any;
  onClick: () => void;
  onDeactivate: () => void;
};

const RecurringReportItem = ({
  title,
  onClick,
  icon,
  isActive,
  onDeactivate,
}: RecurringReportProps) => {
  const { t } = useTranslation();
  const { destructiveToText } = useCDK();
  return (
    <Container>
      <TitleContainer>
        {icon} {t(title)}
        <StatusContainer isActive={!!isActive}>
          {isActive ? t('Active') : t('Inactive')}
        </StatusContainer>
      </TitleContainer>
      <BodyContainer>
        {t(
          'Recurring report with summarized {{type}} stats for your team or company.',
          {
            type: t(title).toLowerCase(),
          }
        )}
      </BodyContainer>
      <ButtonContainer>
        <Button
          variant='secondary'
          text={
            isActive
              ? t('Edit Report')
              : t('Activate {{title}}', { title: t(title) })
          }
          icon={isActive ? <MdEdit height={15} /> : <BsPlus height={20} />}
          onClick={onClick}
        />
        {isActive && (
          <Button
            variant={destructiveToText}
            text={t('Deactivate')}
            onClick={onDeactivate}
          />
        )}
      </ButtonContainer>
    </Container>
  );
};

export default RecurringReportItem;
